import React, { useEffect, useMemo } from "react";

import PostAddIcon from "@mui/icons-material/PostAdd";
import {
  Tabs,
  Tab,
  Paper,
  Button,
  Box,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Link,
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { ShippingCaseResponse } from "@pages/shippingView/types";

import { RootState, useAppDispatch } from "@store/index";
import {
  cleanShippingCasesPricesCustomers,
  fetchShippingCasesPricesCustomers,
  hideWarning,
  hideWarningByEntity,
  sendDataInvoice,
} from "./incomeStatementSlice";
import { useSelector } from "react-redux";
import { ShippingCasesPricesByIdStatus } from "./types";
import Warning from "@atoms/warning";
import { INCOME_STATEMENT_FEATURE } from "@pages/shippingView/config";
import { Send } from "@mui/icons-material";
import { selectISErrorState } from "./selectors";

const tabLabels = ["Canceled Services"];

const tabs = [{ label: "Canceled Services", path: "canceled-services" }];

export default function IncomeStatementLayout() {
  const location = useLocation();

  const { sid, customerId } = useParams<{ sid: string; customerId?: string }>();

  const navigate = useNavigate();

  const { items: customers, loading: customersLoading } = useSelector(
    (state: RootState) =>
      state.viewShipping.incomeStatement.shippingCasesPricesCustomers,
  );

  const { error } = useSelector(selectISErrorState);

  const { success, loading: invoiceLoading } = useSelector(
    (state: RootState) => state.viewShipping.incomeStatement.invoice,
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!INCOME_STATEMENT_FEATURE) {
      navigate("..", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    dispatch(
      fetchShippingCasesPricesCustomers(Number(sid), {
        status__neq: ShippingCasesPricesByIdStatus.CANCELED,
      }),
    );

    return () => {
      dispatch(cleanShippingCasesPricesCustomers());
    };
  }, [dispatch, sid]);

  const { items, loading } = useSelector(
    (state: RootState) =>
      state.viewShipping.incomeStatement.shippingCasesPricesCustomers,
  );

  const { shipping } = useOutletContext<{ shipping: ShippingCaseResponse }>();
  const customer = shipping.offer?.customer;

  const isUpdateSelectedServices = useMatch(
    "/offers/:oid/shipping-case/:sid/income-statement/selected-services/update",
  );

  const isUpdateCanceledServices = useMatch(
    "/offers/:oid/shipping-case/:sid/income-statement/canceled-services/update",
  );

  const role = useSelector((state: RootState) => state.auth.user?.role) || "";

  const isWmsOperations = role === "WMS+Operations";
  const isShowTabs = !isUpdateSelectedServices && !isUpdateCanceledServices;

  const dynamicTabs = useMemo(() => {
    const customerId = customer?.id;

    const mappedItems = items.map((item) => ({
      id: item.id,
      label: item.name,
      path: `selected-services/${item.id}`,
    }));

    if (!customerId) {
      return [...mappedItems, ...tabs];
    }

    const priority = mappedItems.find((i) => i.id === customerId);
    const rest = mappedItems.filter((i) => i.id !== customerId);

    return priority ? [priority, ...rest, ...tabs] : [...mappedItems, ...tabs];
  }, [items, customer?.id]);

  const activeTab = dynamicTabs.findIndex((t) => {
    return location.pathname.endsWith(`/${t.path}`);
  });
  const safeActiveTab = activeTab >= 0 ? activeTab : 0;

  const selectedServices = customerId
    ? "selected-services"
    : "canceled-services";

  useEffect(() => {
    if (
      customers.length > 0 &&
      location.pathname.endsWith("selected-services")
    ) {
      const matched = customers.find((c) => c.id === customer?.id);

      if (matched) {
        navigate(`selected-services/${matched.id}`);
      }
    }
  }, [customers, customer?.id, location.pathname, navigate]);

  useEffect(() => {
    const isCurrentCustomerExists = customers.some(
      (customer) => customer.id === Number(customerId),
    );

    if (
      customerId &&
      customers.length > 0 &&
      !isCurrentCustomerExists &&
      !location.pathname.endsWith("selected-services")
    ) {
      navigate(`selected-services/${customers[0].id}`);
    }

    if (customers.length === 0 && !customersLoading && customerId) {
      navigate("canceled-services");
    }
  }, [location.pathname, navigate, customers, customerId, customersLoading]);

  if (loading) return <CircularProgress size={20} sx={{ mx: "auto" }} />;

  const closeCallback = () => {
    dispatch(hideWarning());
    dispatch(hideWarningByEntity("invoice"));
  };
  const handleSendData = () => {
    dispatch(sendDataInvoice(Number(sid)));
  };

  return (
    <>
      {!!error && (
        <Warning
          text={error}
          severity="error"
          closeCallback={() => closeCallback()}
        />
      )}
      {!!success && (
        <Warning
          text={success}
          severity="success"
          closeCallback={() => closeCallback()}
        />
      )}
      {isShowTabs && !isWmsOperations && (
        <Box display="flex" justifyContent="flex-end" mb={2} gap={1.5}>
          <Tooltip title="Send data to Rule Engine for prices processing">
            <Button
              disabled={invoiceLoading}
              startIcon={
                invoiceLoading ? (
                  <CircularProgress size={20} sx={{ color: "#d1d1d1" }} />
                ) : (
                  <Send />
                )
              }
              onClick={handleSendData}
            >
              Send
            </Button>
          </Tooltip>
          <Button
            startIcon={<PostAddIcon />}
            component={Link}
            to={`${selectedServices}/update`}
          >
            Manage Services
          </Button>
        </Box>
      )}
      <Paper
        elevation={0}
        sx={{
          ...(isShowTabs
            ? {
                p: "16px",
                border: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: "0 4px 4px 4px",
              }
            : {}),
        }}
      >
        {isShowTabs && (
          <>
            <Tabs
              className="cargo"
              value={safeActiveTab}
              sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}
            >
              {dynamicTabs.map((t) => (
                <Tab
                  component={Link}
                  to={t.path}
                  key={t.path}
                  label={t.label}
                />
              ))}
            </Tabs>
          </>
        )}
        <Outlet
          context={{
            tabLabels,
            activeTab: safeActiveTab,
            shipping,
            tabId: tabs[safeActiveTab]?.path ?? tabs[0].path,
          }}
        />
      </Paper>
    </>
  );
}
